// src/utils/ecrDummy.ts
import JSZip from "jszip";

/** ECR row shape (dummy) */
export type EcrRow = {
  UAN: string;
  MemberName: string;
  GrossWages: number;
  EPFWages: number;
  EPSWages: number;
  EDLIWages: number;
  EmployeeShareEPF: number;
  EmployerShareEPF: number;
  EmployerShareEPS: number;
  NCPDays: number;
  RefundAdvances: number;
};

export type ChallanRow = {
  Month: string;
  Year: number;
  EstablishmentId: string;
  NoOfEmployees: number;
  EmployeeShareEPF: number;
  EmployerShareEPF: number;
  EmployerShareEPS: number;
  EDLI: number;
  AdminCharges: number;
  Total: number;
};

export type NcpSummaryRow = {
  UAN: string;
  MemberName: string;
  NCPDays: number;
};

/** Escape and join array of objects into CSV by header order */
export const buildCSV = <T extends Record<string, any>>(rows: T[], headers: (keyof T)[]): string => {
  const esc = (v: any) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const headerLine = headers.join(",");
  const lines = rows.map((r) => headers.map((h) => esc(r[h as string])).join(","));
  return [headerLine, ...lines].join("\n");
};

/** Deterministic-looking dummy members for ECR */
export const getDummyEcrRows = (count = 10): EcrRow[] => {
  const names = [
    "Amit Sharma",
    "Neha Verma",
    "Ravi Kumar",
    "Priya Singh",
    "Arjun Mehta",
    "Sonal Gupta",
    "Rahul Yadav",
    "Meera Iyer",
    "Karan Patel",
    "Pooja Nair",
  ];
  const rows: EcrRow[] = [];
  for (let i = 0; i < count; i++) {
    const base = 25000 + i * 1350;
    const gross = Math.round(base + (i % 5) * 800); // stable-ish variation
    const epfWages = Math.min(gross, 15000); // typical cap
    const epsWages = epfWages;
    const edliWages = epfWages;
    const employeeEPF = Math.round(epfWages * 0.12);
    const employerEPS = Math.round(epfWages * 0.0833);
    const employerEPF = Math.round(epfWages * 0.0367);

    rows.push({
      UAN: `1000${String(123450 + i)}`,
      MemberName: names[i % names.length],
      GrossWages: gross,
      EPFWages: epfWages,
      EPSWages: epsWages,
      EDLIWages: edliWages,
      EmployeeShareEPF: employeeEPF,
      EmployerShareEPF: employerEPF,
      EmployerShareEPS: employerEPS,
      NCPDays: i % 3, // 0..2
      RefundAdvances: 0,
    });
  }
  return rows;
};
type ExtraZipFile =
  | { name: string; url: string }        // browser-friendly: fetch as ArrayBuffer
  | { name: string; data: ArrayBuffer }  // if you already have the bytes

const fetchAsArrayBuffer = async (url: string) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch ${url}`);
  return await res.arrayBuffer();
};

/** Build the three CSVs + readme, return a Blob ZIP */
export const buildEcrBundleZip = async (
  month: string,
  year: number,
  extraFiles: ExtraZipFile[] = []
): Promise<Blob> => {
  const ecrRows = getDummyEcrRows(10);

  const challan = {
    Month: month,
    Year: year,
    EstablishmentId: "DL/12345/678",
    NoOfEmployees: ecrRows.length,
    EmployeeShareEPF: ecrRows.reduce((s, r) => s + r.EmployeeShareEPF, 0),
    EmployerShareEPF: ecrRows.reduce((s, r) => s + r.EmployerShareEPF, 0),
    EmployerShareEPS: ecrRows.reduce((s, r) => s + r.EmployerShareEPS, 0),
    EDLI: Math.round(ecrRows.reduce((s, r) => s + r.EDLIWages, 0) * 0.005),
    AdminCharges: 0,
    Total: 0,
  };
  challan.Total =
    challan.EmployeeShareEPF +
    challan.EmployerShareEPF +
    challan.EmployerShareEPS +
    challan.EDLI +
    challan.AdminCharges;

  const ncpSummary = ecrRows.map((r) => ({
    UAN: r.UAN,
    MemberName: r.MemberName,
    NCPDays: r.NCPDays,
  }));

  const headersECR:any = [
    "UAN","MemberName","GrossWages","EPFWages","EPSWages","EDLIWages",
    "EmployeeShareEPF","EmployerShareEPF","EmployerShareEPS","NCPDays","RefundAdvances",
  ] as const;
  const headersChallan:any = [
    "Month","Year","EstablishmentId","NoOfEmployees",
    "EmployeeShareEPF","EmployerShareEPF","EmployerShareEPS","EDLI","AdminCharges","Total",
  ] as const;
  const headersNCP:any = ["UAN","MemberName","NCPDays"] as const;

  const ecrCSV = buildCSV(ecrRows, headersECR);
  const challanCSV = buildCSV([challan], headersChallan);
  const ncpCSV = buildCSV(ncpSummary, headersNCP);

  const stamp = `${month}_${year}`;
  const readme = `Dummy EPFO ECR Bundle
Month: ${month}
Year: ${year}

Files:
- ECR_${stamp}.csv
- Challan_${stamp}.csv
- NCP_Summary_${stamp}.csv
- (optional) GPF forms as PDFs
`;

  const zip = new JSZip();
  zip.file(`ECR_${stamp}.csv`, ecrCSV);
  zip.file(`Challan_${stamp}.csv`, challanCSV);
  zip.file(`NCP_Summary_${stamp}.csv`, ncpCSV);
  zip.file(`README_${stamp}.txt`, readme);

  // attach extras (PDFs etc.)
  for (const file of extraFiles) {
    if ("data" in file) {
      zip.file(file.name, file.data);
    } else {
      const buf = await fetchAsArrayBuffer(file.url);
      zip.file(file.name, buf);
    }
  }

  return zip.generateAsync({ type: "blob" });
};